import D3Asset from '@/editor/assets/d3.asset';
import {
  evaluateOpacityClipEnvelope,
  evaluateVolumeClipEnvelope,
} from '@/editor/clipEnvelopes'
import { applyMediaMaterialMode } from '@/editor/materials/mediaMaterialMode'

export default class VideoAsset extends D3Asset {
  constructor(uuid) {
    super(uuid);
    this._type = 'video';
    this._props.volume = 1;
    this._lastProps.volume = 1;
  }

  get PROPS() {
    const p = super.PROPS;
    p.volume = {
      type: 'number',
      name: 'Volume',
      normalized: true,
      tool: 'VolumeSlider',
    };
    return p;
  }

  async loadFromFile(file) {
    await this._loadFromFile(file);
    this._asset.meta.name = file.name;
    // Test hook: `window.__vybyDelayPrepare = <ms>` simulates slow media prep
    // to exercise the deferred-attach / late-ready recovery paths.
    const testDelay =
      typeof window !== 'undefined' ? Number(window.__vybyDelayPrepare) : 0;
    if (testDelay > 0) {
      this.dbg.warn('__vybyDelayPrepare active', testDelay);
      await new Promise((resolve) => setTimeout(resolve, testDelay));
    }
    await this.prepareVideo();
    this._asset.ready = true;
    this.emit('ready');
  }

  prepareVideo() {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.loop = true;
      video.autoplay = true;
      video.muted = true;
      this.dbg.log('asset', this._asset);
      let done = false;
      video.onerror = (err) => {
        this.dbg.warn('video asset error', err);
        this.emit('asset.error', err);
        done = true;
        reject(err);
      };
      video.oncanplay = () => {
        this.dbg.log('video.oncanplay', done);
        if (done) return;
        this._asset.meta = {
          ...this._asset.meta,
          width: video.videoWidth,
          height: video.videoHeight,
          duration: video.duration,
        };
        video.currentTime = 0;
      };
      video.onseeked = () => {
        done = true;
        this.dbg.info('seeked', done);
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        // return the canvas image as a blob
        canvas.toBlob((blob) => {
          this._asset.thumbnail = blob;
          resolve();
        }, 'image/png');
      };
      video.src = URL.createObjectURL(this._asset.blob);
    });
  }

  initAnimation(settings, zf) {
    const animation = super.initAnimationD2(settings, zf)
    // Ensure default volume and scale/opacity on defaults for V2 (no keys)
    this._props.volume = 1
    if (animation.keyframes[0]) {
      animation.keyframes[0].props.volume = 1
      animation.keyframes[0].delay.volume = 0
      animation.keyframes[0].easing.volume = 'Linear.None'
    }
    if (animation.keyframes[1]) {
      animation.keyframes[1].props.volume = 1
      animation.keyframes[1].delay.volume = 0
    }
    return animation;
  }

  updateFrame(time, localTime, frame) {
    const props = super.updateFrame(time, localTime, frame);
    if (frame?.strip) {
      const baseGain = Number.isFinite(Number(props.volume)) ? Number(props.volume) : 1
      props.volume = evaluateVolumeClipEnvelope(frame.strip, localTime, baseGain).gain
      const baseOpacity = Number.isFinite(Number(props.opacity)) ? Number(props.opacity) : 1
      props.opacity = evaluateOpacityClipEnvelope(frame.strip, localTime, baseOpacity).opacity
      this.applyProps({ volume: props.volume, opacity: props.opacity })
    }
    this.dbg.log('update frame video', time, localTime, props);
    const inRange = !(localTime < 0 || localTime > frame.strip.length)
    // Out-of-range clips are hidden by d3.asset updateFrame visibility; the V2
    // engine guarantees clip-local frames, so no first-frame priming is needed.
    if (!inRange) {
      return props;
    }
    if (frame?.mesh?.material) {
      applyMediaMaterialMode(frame.mesh.material, frame._mediaAlphaInfo, props.opacity)
    }
    if (frame.controller) {
      if (isFinite(props.volume)) {
        if (frame._mediaGain && frame._mediaGain.gain)
          frame._mediaGain.gain.value = props.volume
        else
          frame.controller.volume = Math.max(0, Math.min(1, props.volume))
      }
    }
    return props;
  }

  toolUpdate(update) {
    const ret = super.toolUpdate(update);
    if (update.tool === 'VolumeSlider') {
      ret.volume = update.volume;
    }
    return ret;
  }

  color() {
    return '#0ae709';
  }

  icon() {
    return ['fas', 'film']
  }
}
